import { useRef, useState, MouseEvent } from 'react'
import { LuEllipsisVertical, LuRefreshCw, LuFolderPlus, LuCloudUpload } from 'react-icons/lu'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'

type Props = {
  onRefresh: () => void
  onNewFolder: () => void
  onUpload: (files: File[]) => void
  loading?: boolean
}

export default function FileActionsMenu({ onRefresh, onNewFolder, onUpload, loading }: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  // Actions that open other UI (Collapse, autoFocus) must wait until MUI's
  // Menu Modal finishes closing — otherwise its focus restoration swallows
  // the new UI's focus. We store the callback and fire it in onExited.
  const pendingRef = useRef<(() => void) | null>(null)

  const open = (e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)
  const close = () => setAnchor(null)
  const onExited = () => {
    pendingRef.current?.()
    pendingRef.current = null
  }

  const handleRefresh = () => {
    close()
    onRefresh()
  }
  const handleNewFolder = () => {
    pendingRef.current = onNewFolder
    close()
  }
  const handleUpload = () => {
    close()
    inputRef.current?.click()
  }
  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (files.length) onUpload(files)
    e.target.value = ''
  }

  return (
    <>
      <IconButton size="small" onClick={open} aria-label="File actions">
        <LuEllipsisVertical />
      </IconButton>
      <Menu anchorEl={anchor} open={!!anchor} onClose={close} slotProps={{ transition: { onExited } }}>
        <MenuItem onClick={handleRefresh} disabled={loading}>
          <ListItemIcon>
            <LuRefreshCw />
          </ListItemIcon>
          <ListItemText>Refresh</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleNewFolder}>
          <ListItemIcon>
            <LuFolderPlus />
          </ListItemIcon>
          <ListItemText>New folder</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleUpload}>
          <ListItemIcon>
            <LuCloudUpload />
          </ListItemIcon>
          <ListItemText>Upload files</ListItemText>
        </MenuItem>
      </Menu>
      <input ref={inputRef} type="file" multiple hidden onChange={handleFiles} />
    </>
  )
}
